import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { CourseMappingResource } from '@ab/ams-school-api/types/private/v1';
import { hasKey } from '~/utils';

export type DeleteCourseMappingPayload = {
  courseMapping: CourseMappingResource;
};

export type DeleteCourseMappingResponse = {
  data: CourseMappingResource;
};

export function useDeleteCourseMapping() {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation(
    ['deleteCourseMapping'],
    async (payload: DeleteCourseMappingPayload) => {
      const { courseMapping } = payload;
      const response = await request<DeleteCourseMappingResponse>(
        `${config.apiHost}/private/v1/course_mappings/${courseMapping.id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              type: 'course_mapping',
              id: courseMapping.id,
              attributes: {
                equivalentCourse: courseMapping.attributes.equivalentCourse,
                sourceCourseName: courseMapping.attributes.sourceCourseName,
                sourceCountryCode: courseMapping.attributes.sourceCountry?.alpha2Code,
                sourceEducationLevel: courseMapping.attributes.sourceEducationLevel,
                sourceInstitutionName: courseMapping.attributes.sourceInstitutionName,
                sourceProgramName: courseMapping.attributes.sourceProgramName,
                isArchived: true,
              },
            },
          },
          isExpectedResponse,
        }
      );

      // Invalidate queries to refresh the course mappings list
      queryClient.invalidateQueries({ queryKey: ['courseMappings'] });

      return response.data;
    }
  );

  return {
    deleteCourseMapping: mutateAsync,
    isDeleting: isLoading,
    error,
  };
}

function isExpectedResponse(res: unknown): res is DeleteCourseMappingResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
